import documentFontCatalog from '../../../my-flask-app/assets/fonts/document-fonts.json' with { type: 'json' };

/**
 * Font settings types for document export
 * System-font availability depends on the PC; downloadable fonts include OFL licenses.
 */

// ============ Font Definitions ============

/**
 * System fonts that are commonly available on Windows/Mac
 */
export const SYSTEM_FONTS = [
  // Windows fonts
  { name: '맑은 고딕', value: 'Malgun Gothic', os: 'windows' as const },
  { name: '바탕', value: 'Batang', os: 'windows' as const },
  { name: '굴림', value: 'Gulim', os: 'windows' as const },
  { name: '돋움', value: 'Dotum', os: 'windows' as const },
  // Mac fonts
  { name: 'Apple SD Gothic Neo', value: 'Apple SD Gothic Neo', os: 'mac' as const },
  { name: 'AppleMyungjo', value: 'AppleMyungjo', os: 'mac' as const },
  // Universal fonts
  { name: 'Arial', value: 'Arial', os: 'all' as const },
  { name: 'Times New Roman', value: 'Times New Roman', os: 'all' as const },
  { name: 'Georgia', value: 'Georgia', os: 'all' as const },
  { name: 'Verdana', value: 'Verdana', os: 'all' as const },
] as const;

/** Unmodified OFL fonts mirrored with licenses; generated from official packages. */
export interface DocumentFont {
  value: string;
  name: string;
  provider: string;
  sourceUrl: string;
  licenseUrl: string;
  licenseSource: string;
  downloadUrl: string;
  downloadBytes: number;
  documentFamily: string;
  hwpxFamily: string;
  localFamilies: string[];
  localFaces: string[];
  faces: { file: string; url: string; sha256: string; weight: string }[];
}

const legacyAliases: Record<string, string[]> = {
  'Noto Sans KR': ['본고딕'], 'Noto Serif KR': ['본명조'],
  'Nanum Pen Script': ['NanumPen'], 'Nanum Brush Script': ['NanumBrush'],
  NanumSquare: ['Nanum Square'], NanumSquareRound: ['Nanum Square Round'],
  NanumSquareNeo: ['나눔스퀘어네오', 'NanumSquare Neo TTF', '나눔스퀘어 네오 TTF'],
  NanumBarunGothic: ['Nanum Barun Gothic'], MaruBuri: ['Maru Buri'],
};

export const DOCUMENT_FONTS: DocumentFont[] = documentFontCatalog;
const webFonts = DOCUMENT_FONTS.map(font => ({ ...font,
  localNames: [...font.localFamilies, ...font.localFaces, ...(legacyAliases[font.value] ?? [])],
}));
export const GOOGLE_FONTS = webFonts.filter(font => font.provider === 'google');
export const NAVER_FONTS = webFonts.filter(font => font.provider === 'naver');

export function findFontByNameOrValue(fontFamily: string): {
  name: string; value: string; type: 'system' | 'google' | 'naver'; downloadUrl?: string;
} | undefined {
  const font = webFonts.find(f => f.value === fontFamily || f.name === fontFamily || f.localNames.includes(fontFamily));
  if (font) return { ...font, type: font.provider as 'google' | 'naver' };
  const system = SYSTEM_FONTS.find(f => f.value === fontFamily || f.name === fontFamily);
  return system ? { ...system, type: 'system' } : undefined;
}

export function getDocumentFont(fontFamily: string): DocumentFont | undefined {
  const value = findFontByNameOrValue(fontFamily)?.value;
  return DOCUMENT_FONTS.find(font => font.value === value);
}

export function getFontValue(fontFamily: string): string {
  return findFontByNameOrValue(fontFamily)?.value ?? fontFamily;
}

export function getFontDisplayName(fontFamily: string): string {
  return findFontByNameOrValue(fontFamily)?.name ?? fontFamily;
}

export function isGoogleFont(fontFamily: string): boolean {
  return findFontByNameOrValue(fontFamily)?.type === 'google';
}

export function isNaverFont(fontFamily: string): boolean {
  return findFontByNameOrValue(fontFamily)?.type === 'naver';
}

/** Exact names from shipped font metadata, not speculative aliases. */
export function getLocalFontNames(fontFamily: string): string[] {
  const font = getDocumentFont(fontFamily);
  if (font) return [...font.localFamilies, ...font.localFaces];
  const system = findFontByNameOrValue(fontFamily);
  return system ? [system.value, system.name] : [fontFamily];
}

export function getAllFonts() {
  return [
    { group: '시스템 폰트', fonts: SYSTEM_FONTS },
    { group: 'Google Fonts · 무료 다운로드', fonts: GOOGLE_FONTS },
    { group: '네이버 폰트 · 무료 다운로드', fonts: NAVER_FONTS },
  ];
}

// ============ Font Settings Types ============

/**
 * Settings for a single font element
 * @property fontFamily - Font family name (e.g., 'Malgun Gothic')
 * @property fontSize - Font size in points (pt) - Word/HWP standard
 * @property fontColor - Font color as hex string (e.g., '#000000')
 * @property lineSpacing - Line spacing multiplier (only for passage and explanation)
 * @property letterSpacing - Letter spacing (자간) adjustment for HWPX export (-20 to +20)
 */
export interface FontElementSettings {
  fontFamily: string;
  fontSize: number;
  fontColor: string;
  lineSpacing?: number;
  /** Missing = a multiplier; exact = baseline distance in points. */
  lineSpacingMode?: 'multiple' | 'exact';
  letterSpacing?: number;
}

/**
 * Complete font settings for all 4 document elements
 */
export interface FontSettings {
  /** 유형 제목 (Question type title) */
  title: FontElementSettings;
  /** 문제 지시문 (Question instruction line, e.g., "1. 다음을 읽고 물음에 답하시오.") */
  passageNumber: FontElementSettings;
  /** 지문 (Passage text) */
  passage: FontElementSettings;
  /** 해설지 (Explanation/Answer key) */
  explanation: FontElementSettings;
}

/**
 * Font settings state with sync metadata
 * updatedAt is stored separately from FontSettings to avoid including it in presets
 */
export interface FontSettingsState {
  settings: FontSettings;
  /** Server-set timestamp for sync conflict resolution */
  updatedAt: number;
}

/**
 * Saved font preset with metadata
 */
export interface FontPreset {
  id: string;
  name: string;
  settings: FontSettings;
  createdAt: number;
}

// ============ Constraints ============

/**
 * Validation constraints for font settings
 */
export const FONT_CONSTRAINTS = {
  /** Minimum font size in pt */
  SIZE_MIN: 6,
  /** Maximum font size in pt */
  SIZE_MAX: 24,
  /** Minimum line spacing multiplier */
  LINE_SPACING_MIN: 1.0,
  /** Maximum line spacing multiplier */
  LINE_SPACING_MAX: 3.0,
  /** Minimum letter spacing (tighter) */
  LETTER_SPACING_MIN: -20,
  /** Maximum letter spacing (wider) */
  LETTER_SPACING_MAX: 20,
};

// ============ Default Values ============

/**
 * Default font settings for the document preview and web export
 */
export const DEFAULT_FONT_SETTINGS: FontSettings = {
  title: {
    fontFamily: 'Malgun Gothic',
    fontSize: 12,
    fontColor: '#335581',
    letterSpacing: 0,
  },
  passageNumber: {
    fontFamily: 'Malgun Gothic',
    fontSize: 8,
    fontColor: '#000000',
    letterSpacing: 0,
  },
  passage: {
    fontFamily: 'Malgun Gothic',
    fontSize: 8,
    fontColor: '#000000',
    lineSpacing: 1.2,
    letterSpacing: 0,
  },
  explanation: {
    fontFamily: 'Malgun Gothic',
    fontSize: 7,
    fontColor: '#000000',
    lineSpacing: 1.1,
    letterSpacing: 0,
  },
};

/**
 * Fallback fonts for export when user's selected font is not available
 */
export const FALLBACK_FONTS = {
  windows: 'Malgun Gothic',
  mac: 'Apple SD Gothic Neo',
  default: 'Arial',
};

// ============ Storage Keys ============

/** LocalStorage key for font settings */
export const FONT_SETTINGS_STORAGE_KEY = 'autoqm_font_settings';

/** LocalStorage key for font presets */
export const FONT_PRESETS_STORAGE_KEY = 'autoqm_font_presets';

/** Maximum number of font presets */
export const MAX_FONT_PRESETS = 10;

// ============ Context Types ============

/**
 * Font settings context value type
 */
export interface FontSettingsContextValue {
  /** Current font settings */
  settings: FontSettings;
  /** Server sync timestamp */
  updatedAt: number;
  /** Update all or partial font settings */
  setSettings: (settings: Partial<FontSettings>) => void;
  /** Update a specific font element */
  updateElement: (
    element: keyof FontSettings,
    values: Partial<FontElementSettings>
  ) => void;
  /** Reset to default settings */
  resetSettings: () => void;
  /** Validate a font size value */
  validateFontSize: (value: number) => number;
  /** Validate a line spacing value */
  validateLineSpacing: (value: number) => number;
  /** List of saved presets */
  presets: FontPreset[];
  /** Save current settings as a new preset */
  savePreset: (name: string) => void;
  /** Load a preset by ID */
  loadPreset: (id: string) => void;
  /** Delete a preset by ID */
  deletePreset: (id: string) => void;
  /** Rename a preset */
  renamePreset: (id: string, newName: string) => void;
  /** Whether server sync is in progress */
  isSyncing: boolean;
  /** Whether initial hydration from server is in progress */
  isHydrating: boolean;
  syncError: string | null;
  loadFailed: boolean;
  retrySync: () => void;
}

// ============ Utility Functions ============

/**
 * Validate font settings data from localStorage or server
 */
export function validateFontSettings(data: unknown): FontSettings | null {
  if (!data || typeof data !== 'object') return null;

  const settings = data as Record<string, unknown>;
  const elements: (keyof FontSettings)[] = ['title', 'passageNumber', 'passage', 'explanation'];

  for (const element of elements) {
    if (!settings[element] || typeof settings[element] !== 'object') {
      return null;
    }
    const elem = settings[element] as Record<string, unknown>;
    if (typeof elem.fontFamily !== 'string') return null;
    if (typeof elem.fontSize !== 'number') return null;
    if (typeof elem.fontColor !== 'string') return null;
    // lineSpacing is optional for title and passageNumber
    if (elem.lineSpacing !== undefined && typeof elem.lineSpacing !== 'number') {
      return null;
    }
    if (elem.lineSpacingMode !== undefined && !['multiple', 'exact'].includes(String(elem.lineSpacingMode))) return null;
  }

  return data as FontSettings;
}

/**
 * Clamp font settings values to valid ranges
 */
export function clampFontSettings(settings: FontSettings): FontSettings {
  const finite = (value: unknown, fallback: number, min: number, max: number) =>
    typeof value === 'number' && Number.isFinite(value) ? Math.min(max, Math.max(min, value)) : fallback;
  return Object.fromEntries((Object.keys(DEFAULT_FONT_SETTINGS) as (keyof FontSettings)[]).map((key) => {
    const fallback = DEFAULT_FONT_SETTINGS[key];
    const value = settings[key] ?? fallback;
    const fontSize = finite(value.fontSize, fallback.fontSize, FONT_CONSTRAINTS.SIZE_MIN, FONT_CONSTRAINTS.SIZE_MAX);
    return [key, {
      fontFamily: typeof value.fontFamily === 'string' && value.fontFamily.trim()
        ? value.fontFamily.trim().slice(0, 100) : fallback.fontFamily,
      fontSize,
      fontColor: /^#[0-9a-f]{6}$/i.test(value.fontColor) ? value.fontColor : fallback.fontColor,
      letterSpacing: Math.trunc(finite(value.letterSpacing, 0, FONT_CONSTRAINTS.LETTER_SPACING_MIN, FONT_CONSTRAINTS.LETTER_SPACING_MAX)),
      ...(fallback.lineSpacing !== undefined || value.lineSpacing !== undefined ? {
        lineSpacing: value.lineSpacingMode === 'exact'
          ? finite(value.lineSpacing, Math.max(fontSize, 11.7), fontSize, 72)
          : finite(value.lineSpacing, fallback.lineSpacing ?? 1.2, FONT_CONSTRAINTS.LINE_SPACING_MIN, FONT_CONSTRAINTS.LINE_SPACING_MAX),
        ...(value.lineSpacingMode === 'exact' ? { lineSpacingMode: 'exact' as const } : {}),
      } : {}),
    }];
  })) as unknown as FontSettings;
}

/**
 * Convert pt to px for CSS rendering (1pt ≈ 1.333px)
 */
export function ptToPx(pt: number): number {
  return pt * 1.333;
}

/**
 * Convert line spacing multiplier to CSS line-height
 */
export function lineSpacingToCss(spacing: number): string {
  return spacing.toString();
}
